import { Injectable } from '@angular/core';
import {
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpInterceptor,
    HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AppConstant } from '../app.constant';
import { UtilityHelper } from '../helpers/utilityHelper';
import { AuthenticationHelper } from '../helpers/authenticationHelper';
import { ToastsManager } from '../helpers/toastsManager';
import { TranslateService } from '@ngx-translate/core';

/**
 * AuthHttpInterceptor
 * ───────────────────
 * Intercepts HTTP requests and handles 401 Unauthorized errors automatically:
 * - Clears session credentials.
 * - Displays a session expired toast notification.
 * - Redirects Hub users to /mentor-hub/login.
 * - Redirects other users to /signout.
 *
 * Skips toast/redirect while signing out or when already logged out — otherwise
 * in-flight / teardown API calls after the token is cleared spam "session expired".
 */
@Injectable()
export class AuthHttpInterceptor implements HttpInterceptor {

    constructor(
        private router: Router,
        private utility: UtilityHelper,
        private authService: AuthenticationHelper,
        private toastr: ToastsManager,
        private translate: TranslateService
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                    const url = req.url.toLowerCase();
                    // Skip handling for authentication endpoints to avoid infinite redirect loops on invalid credentials
                    const isAuthEndpoint = url.includes('/sessions') ||
                                           url.includes('/login') ||
                                           url.includes('/password');

                    const signingOut = AppConstant.isSignOutInProgress();
                    const alreadyLoggedOut = !this.authService.isLoggedIn();
                    const onSignOutRoute =
                        (this.router.url || window.location.pathname || '').includes('/signout');

                    if (
                        !isAuthEndpoint &&
                        !signingOut &&
                        !alreadyLoggedOut &&
                        !onSignOutRoute
                    ) {
                        this.authService.stopMonitoring();
                        this.utility.clear();

                        const sessionExpiredMsg = this.translate.instant('mentorHub.toasts.sessionExpired') ||
                                                  'Your session has expired. Please log in again.';
                        this.toastr.error(sessionExpiredMsg);

                        const currentUrl = this.router.url || window.location.pathname;
                        const isMentorHubRoute = (currentUrl === '/mentor-hub' || currentUrl.startsWith('/mentor-hub/')) && !currentUrl.includes('/mentor-hub/login');
                        if (isMentorHubRoute) {
                            this.router.navigate(['/mentor-hub/login']);
                        } else if (!currentUrl.includes('/sign-in')) {
                            this.router.navigate(['/signout']);
                        }
                    }
                }
                return throwError(() => error);
            })
        );
    }
}
